import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, CheckSquare, FolderKanban, FileText, Calendar,
  Send, Sparkles, Settings as SettingsIcon, LogOut, Menu, Command, Plus, Puzzle, HardDrive,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/notes", label: "Notes", icon: FileText },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/files", label: "Files", icon: HardDrive },
  { to: "/telegram", label: "Telegram", icon: Send },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    api.get("/workspaces").then((r) => setWorkspaces(r.data)).catch(() => {});
  }, []);

  const activeWs = workspaces.find((w) => w.is_active);
  const switchWorkspace = async (id) => {
    if (!id || (activeWs && id === activeWs.id)) return;
    await api.post("/workspace/switch", { workspace_id: id });
    window.location.href = "/"; // reload so every page refetches the new workspace's data
  };

  const initials = (user?.name || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-950 text-white flex items-center justify-center rounded-sm">
            <Command size={18} />
          </div>
          <div>
            <div className="font-black tracking-tighter text-lg leading-none">EXECUTIVE OS</div>
            <div className="label-mono text-muted-foreground mt-0.5">Operational Workspace</div>
          </div>
        </div>
      </div>

      {workspaces.length > 0 && (
        <div className="px-5 py-3 border-b border-border">
          <div className="label-mono text-muted-foreground mb-1">Organization</div>
          <select
            value={activeWs?.id || ""}
            onChange={(e) => switchWorkspace(e.target.value)}
            data-testid="workspace-switcher"
            className="w-full text-sm font-semibold bg-muted rounded-sm px-2 py-1.5 border border-border cursor-pointer"
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}{w.role === "owner" ? " • owner" : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                isActive ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-muted hover:text-slate-950"
              }`
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}

        <div className="pt-3 mt-3 border-t border-border">
          <NavLink
            to="/copilot"
            onClick={() => setOpen(false)}
            data-testid="nav-copilot"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold transition-colors ${
                isActive ? "bg-ai text-white" : "text-ai hover:bg-ai/10"
              }`
            }
          >
            <Sparkles size={18} />
            AI Copilot
          </NavLink>
        </div>
      </nav>

      <div className="px-3 py-4 border-t border-border space-y-1">
        <NavLink
          to="/plugins"
          onClick={() => setOpen(false)}
          data-testid="nav-plugins"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              isActive ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-muted hover:text-slate-950"
            }`
          }
        >
          <Puzzle size={18} /> Plugins
        </NavLink>
        <NavLink
          to="/settings"
          onClick={() => setOpen(false)}
          data-testid="nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              isActive ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-muted hover:text-slate-950"
            }`
          }
        >
          <SettingsIcon size={18} /> Settings
        </NavLink>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-sm bg-slate-950 text-white flex items-center justify-center text-xs font-bold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button onClick={logout} data-testid="logout-button" className="text-muted-foreground hover:text-destructive">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-secondary">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-border flex-col fixed inset-y-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-white border-r border-border">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-border px-4 h-14 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu size={22} />
          </button>
          <span className="font-black tracking-tighter">EXECUTIVE OS</span>
          <Button
            size="sm"
            className="rounded-sm bg-ai hover:bg-blue-800"
            onClick={() => navigate("/voice")}
            data-testid="mobile-capture-button"
          >
            <Plus size={16} />
          </Button>
        </header>

        <main className="flex-1 p-5 md:p-8 lg:p-10 max-w-[1500px] w-full mx-auto fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
